/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { hasAlertSenderRole } from '@buxus/shared'
import { abpAdapter } from './adapter'
import { setAuthSessionFailureHandler } from './authSessionFailure'
import type { AbpBootstrapData } from './types'

type AbpContextValue = {
  ready: boolean
  bootstrap: AbpBootstrapData | null
  l: (key: string, sourceName?: string) => string
  isAuthenticated: boolean
  sessionContextLoaded: boolean
  roleNames: readonly string[]
  canSendAlerts: boolean
  markAuthenticated: (userId?: string) => void
  clearSession: () => void
  probeSession: () => Promise<boolean>
  logout: () => Promise<void>
  can: (permission: string) => boolean
  featureEnabled: (name: string, fallback?: boolean) => boolean
  cultureName: string
  setCulture: (cultureName: string) => Promise<void>
}

const AbpContext = createContext<AbpContextValue | null>(null)

/**
 * Registers 401 handling before sibling providers' effects run (layout phase, first child order).
 * Clears client auth and returns the shell to the login route.
 */
function AuthSessionFailureBridge({ onClear }: { onClear: () => void }) {
  const navigate = useNavigate()
  useLayoutEffect(() => {
    const run = () => {
      onClear()
      navigate('/', { replace: true })
    }
    setAuthSessionFailureHandler(run)
    return () => setAuthSessionFailureHandler(null)
  }, [navigate, onClear])
  return null
}

export function AbpProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [bootstrap, setBootstrap] = useState<AbpBootstrapData | null>(null)
  // Local token+userId are enough for UI gates that only need "are we signed in?"
  // (register/invite). Remote probeSession still hydrates roles/profile for the app shell.
  const [isAuthenticated, setIsAuthenticated] = useState(() => abpAdapter.isAuthenticated())
  const [cultureName, setCultureName] = useState('en')

  useEffect(() => {
    let mounted = true
    const onRegistrationHost =
      typeof window !== 'undefined' &&
      (/^\/user\/(register|invite)(\/|$)/.test(window.location.pathname) ||
        /^\/register\/scenarios(\/|$)/.test(window.location.pathname))

    void abpAdapter.bootstrap().then((b) => {
      if (!mounted) return
      setBootstrap(b)
      setCultureName(abpAdapter.getCulture())

      // Register/invite only needs local signed-in to steer away. Do not run probeSession
      // (application-configuration + get-registration-profile) — that is app-shell hydration
      // and must not race Log Out with profile-fail teardown / login error modals.
      if (onRegistrationHost) {
        setIsAuthenticated(abpAdapter.isAuthenticated())
        setReady(true)
        return
      }

      void abpAdapter.probeSession().then((ok) => {
        if (!mounted) return
        setBootstrap(abpAdapter.getBootstrap())
        // Keep local-session truth on transient probe failures (network); only drop when
        // probe invalidated storage (401/logout path).
        setIsAuthenticated(ok || abpAdapter.isAuthenticated())
        setReady(true)
      })
    })
    return () => {
      mounted = false
    }
  }, [])

  const clearSession = useMemo(
    () => () => {
      abpAdapter.clearSession()
      setIsAuthenticated(false)
    },
    [],
  )

  const value = useMemo<AbpContextValue>(
    () => ({
      ready,
      bootstrap,
      l: (key, sourceName) => abpAdapter.l(key, sourceName),
      isAuthenticated,
      markAuthenticated: (userId) => {
        abpAdapter.markAuthenticated(userId)
        setIsAuthenticated(true)
      },
      clearSession,
      probeSession: async () => {
        const ok = await abpAdapter.probeSession()
        setBootstrap(abpAdapter.getBootstrap())
        setIsAuthenticated(ok || abpAdapter.isAuthenticated())
        return ok
      },
      sessionContextLoaded: bootstrap?.sessionContextLoaded ?? false,
      roleNames: bootstrap?.roleNames ?? [],
      canSendAlerts:
        (bootstrap?.sessionContextLoaded ?? false) &&
        hasAlertSenderRole(bootstrap?.roleNames),
      logout: async () => {
        await abpAdapter.logout()
        setBootstrap(abpAdapter.getBootstrap())
        setIsAuthenticated(false)
      },
      can: (permission) => abpAdapter.can(permission),
      featureEnabled: (name, fallback) => abpAdapter.featureEnabled(name, fallback),
      cultureName,
      setCulture: async (nextCultureName) => {
        await abpAdapter.setCulture(nextCultureName)
        setCultureName(nextCultureName)
      },
    }),
    [ready, bootstrap, isAuthenticated, cultureName, clearSession],
  )

  return (
    <AbpContext.Provider value={value}>
      <AuthSessionFailureBridge onClear={clearSession} />
      {children}
    </AbpContext.Provider>
  )
}

export function useAbp(): AbpContextValue {
  const v = useContext(AbpContext)
  if (!v) throw new Error('useAbp must be used within AbpProvider')
  return v
}

